import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import Home from './components/Home';
import Finance from './components/Finance';
import Health from './components/Health';
import Goals from './components/Goals';
import Connect from './components/Connect';

import { loadData, saveData } from './utils/storage';
import { DEFAULT_DATA } from './utils/defaults';
import { api } from './utils/api';
import './index.css';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const ALLOWED_EMAILS = ['josh@joshwhitcomb.com'];

const PILLAR_NAV = [
  {
    id: 'finance', label: 'Finance',
    icon: '💰',
    svgIcon: (
      <svg viewBox="0 0 32 32" width="28" height="28">
        <rect x="1" y="1" width="30" height="30" rx="5" fill="#508991"/>
        <rect x="5" y="20" width="6" height="9" rx="1.5" fill="#172A3A"/>
        <rect x="13" y="13" width="6" height="16" rx="1.5" fill="#172A3A"/>
        <rect x="21" y="7" width="6" height="22" rx="1.5" fill="#172A3A"/>
        <rect x="3" y="28" width="26" height="2.5" rx="1" fill="#172A3A"/>
      </svg>
    ),
    color: '#2C6E6E', bg: '#E8F4F4', description: 'Net worth, retirement, investments & the wheel'
  },
  {
    id: 'health', label: 'Health',
    icon: '❤️',
    svgIcon: (
      <svg viewBox="0 0 32 32" width="28" height="28">
        <path d="M16 29L3 16C0.5 13.5 0.5 9 3 6.5S10 4 12.5 6.5L16 10l3.5-3.5C22 4 26.5 4 29 6.5S31.5 13 29 15.5z" fill="#508991"/>
        <polyline points="5,17 8,11 11.5,21 15.5,12 18.5,17 21,14.5 24,17" stroke="#172A3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    color: '#8B3A3A', bg: '#F5ECEC', description: 'Weight, labs, daily habits & progress'
  },
  {
    id: 'habits', label: 'Habits',
    icon: '🔥',
    svgIcon: (
      <svg viewBox="0 0 32 32" width="28" height="28">
        <circle cx="16" cy="16" r="14" fill="#508991"/>
        <circle cx="16" cy="16" r="10" fill="#172A3A"/>
        <circle cx="16" cy="16" r="7" fill="#508991"/>
        <circle cx="16" cy="16" r="4" fill="#172A3A"/>
        <circle cx="16" cy="16" r="2.5" fill="#508991"/>
      </svg>
    ),
    color: '#8B5A1A', bg: '#F5EFE5', description: 'Reading, deep work & daily keystones'
  },
  {
    id: 'connect', label: 'Connect',
    icon: '👨‍👩‍👧‍👦',
    svgIcon: (
      <svg viewBox="0 0 32 32" width="28" height="28">
        <circle cx="21" cy="9" r="5" fill="#508991" opacity="0.6"/>
        <path d="M14 28c0-5 3.1-8 7-8s7 3 7 8z" fill="#508991" opacity="0.6"/>
        <circle cx="12" cy="9" r="5.5" fill="#508991"/>
        <path d="M2 28c0-5.5 4-9 10-9s10 3.5 10 9z" fill="#508991"/>
        <circle cx="12" cy="8.5" r="2.5" fill="#172A3A"/>
        <circle cx="21" cy="8.5" r="2" fill="#172A3A" opacity="0.8"/>
      </svg>
    ),
    color: '#2C5A8B', bg: '#E8EFF5', description: 'Jenny, Miles, Ellie & friendships'
  },
];

function LoginScreen({ onSuccess }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 style={{ fontFamily: 'DM Serif Display, serif', fontSize: '2.5rem', color: 'var(--accent)', marginBottom: '8px' }}>Whitcomb Dashboard</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>Your life, at a glance.</p>
      </div>
      <GoogleLogin onSuccess={onSuccess} onError={() => alert('Sign in failed')} />
    </div>
  );
}

function DarkNav({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const activePillar = PILLAR_NAV.find(p => location.pathname === '/' + p.id)?.id;

  return (
    <>
      <nav className="top-nav">
        <div className="top-nav-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <img src="/jw-monogram-nav.png" alt="JW" style={{ height: 36, cursor: 'pointer' }} onClick={() => navigate('/')} />
            {PILLAR_NAV.map(p => (
              <button key={p.id} onClick={() => navigate('/' + p.id)} className={`top-nav-btn ${activePillar === p.id ? 'active' : ''}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>{user?.name?.split(' ')[0]}</span>
            <button onClick={onLogout} className="top-nav-signout">Sign out</button>
          </div>
        </div>
      </nav>

      <nav className="bottom-nav">
        <button className={`bottom-nav-btn ${!activePillar ? 'active' : ''}`} onClick={() => navigate('/')}>
          <span className="bottom-nav-icon"><img src="/jw-monogram-nav.png" alt="JW" style={{ width: 28, height: 28, objectFit: "contain" }} /></span>
          <span className="bottom-nav-label">Home</span>
        </button>
        {PILLAR_NAV.map(p => (
          <button key={p.id} className={`bottom-nav-btn ${activePillar === p.id ? 'active' : ''}`} onClick={() => navigate('/' + p.id)}>
            <span className="bottom-nav-icon">{p.svgIcon}</span>
            <span className="bottom-nav-label">{p.label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}

function PillarPage({ pillar, data, updateData }) {
  const COMPONENTS = {
    finance: <Finance data={data} updateData={updateData} />,
    health:  <Health  data={data} updateData={updateData} />,
    habits:  <Goals   data={data} updateData={updateData} />,
    connect: <Connect />,
  };
  const config = PILLAR_NAV.find(p => p.id === pillar);
  return (
    <main className="pillar-main">
      <div className="pillar-header">
        <h1>{config.label}</h1>
        <p>{config.subtitle}</p>
      </div>
      {COMPONENTS[pillar]}
    </main>
  );
}

function AppInner() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('wd_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [data, setData] = useState(() => loadData() || DEFAULT_DATA);
  const navigate = useNavigate();

  useEffect(() => { saveData(data); }, [data]);

  useEffect(() => {
    if (!user) return;
    Promise.allSettled([
      api.getAccounts(),
      api.getHistory(),
      api.getIncome(),
      api.getContributions(),
      api.getTrades(),
      api.getShares(),
      api.getWeight(),
      api.getLabs(),
      api.getGoals(),
      api.getHabits(),
      api.getMeditation(),
      api.getBooks(),
    ]).then(([accounts, history, income, contributions, trades, shares, weight, labs, goals, habits, meditation, books]) => {
      setData(prev => ({
        ...prev,
        accounts:      accounts.status      === 'fulfilled' && accounts.value?.length      ? accounts.value      : prev.accounts,
        history:       history.status       === 'fulfilled' && history.value?.length       ? history.value       : prev.history,
        income:        income.status        === 'fulfilled' && income.value                ? income.value        : prev.income,
        contributions: contributions.status === 'fulfilled' && contributions.value         ? contributions.value : prev.contributions,
        trades:        trades.status        === 'fulfilled' && trades.value?.length        ? trades.value        : prev.trades,
        shares:        shares.status        === 'fulfilled' && shares.value               ? shares.value        : prev.shares,
        weight:        weight.status        === 'fulfilled' && weight.value?.length        ? weight.value        : prev.weight,
        labs:          labs.status          === 'fulfilled' && labs.value?.length          ? labs.value          : prev.labs,
        goals:         goals.status         === 'fulfilled' && goals.value                 ? goals.value         : prev.goals,
        habitLogs:     habits.status        === 'fulfilled' && habits.value?.length        ? habits.value        : prev.habitLogs || [],
        meditation:    meditation.status    === 'fulfilled' && meditation.value            ? meditation.value    : prev.meditation || [],
        books:         books.status         === 'fulfilled' && books.value?.length         ? books.value         : prev.books || [],
      }));
    });
  }, [user]);

  const handleLogin = (credentialResponse) => {
    try {
      const base64 = credentialResponse.credential.split('.')[1];
      const decoded = JSON.parse(atob(base64));
      if (ALLOWED_EMAILS.includes(decoded.email)) {
        localStorage.setItem('wd_user', JSON.stringify({ email: decoded.email, name: decoded.given_name || decoded.name }));
        setUser({ email: decoded.email, name: decoded.given_name || decoded.name });
        navigate('/');
      } else {
        alert('Access denied. This dashboard is private.');
      }
    } catch (e) {
      alert('Sign in failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wd_user');
    setUser(null);
    navigate('/');
  };

  const updateData = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  if (!user) {
    return (
      <GoogleOAuthProvider clientId={CLIENT_ID}>
        <LoginScreen onSuccess={handleLogin} />
      </GoogleOAuthProvider>
    );
  }

  return (
    <div className="app">
      <DarkNav user={user} onLogout={handleLogout} />
      <Routes>
        <Route path="/" element={
          <main className="pillar-main">
            <Home data={data} onSelectPillar={(p) => navigate('/' + p)} user={user} />
          </main>
        } />
        {PILLAR_NAV.map(p => (
          <Route key={p.id} path={'/' + p.id} element={<PillarPage pillar={p.id} data={data} updateData={updateData} />} />
        ))}
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
